<template>
    <div>
        <navbar/>
        <div class="container mt-5 mb-5">
            <div class="policy-container">
                <h1 class="text-center">Terms of Use</h1>
                <p class="text-center text-muted">Last updated: December 1, 2025</p>
                <p>Welcome to <strong>just bookmark it!</strong>, a bookmark management service operated by <strong>Vályi Péter (sole proprietor)</strong>, based in Hungary (“we,” “us,” “our”).</p>
                <p>By creating an account, accessing our website, or using our iOS application (collectively, the “Service”), you agree to these Terms of Use (“Terms”). If you do not agree, do not use the Service.</p>

                <h2>1. Eligibility</h2>
                <ul>
                    <li>You must be <strong>16 years or older</strong> to create an account and use the Service.</li>
                    <li>You represent that you have legal capacity to enter a binding agreement.</li>
                    <li>Business and organizational accounts are permitted.</li>
                </ul>

                <h2>2. Description of the Service</h2>
                <p><strong>just bookmark it!</strong> is a <strong>bookmark management service</strong> that allows users to save, store, organize, and optionally share URLs and associated tags.</p>
                <p>Current features are free for a trial period, and available for a monthly fee after. Users will be notified in advance of any pricing changes.</p>

                <h2>3. Account Registration and Security</h2>
                <ul>
                    <li>You must provide accurate information, including a valid email address.</li>
                    <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                    <li>You must notify us immediately at <a href="mailto:support@justbookmark.it">support@justbookmark.it</a> if you suspect unauthorized access.</li>
                    <li>We may suspend or terminate accounts for violations of these Terms.</li>
                </ul>

                <h2>4. User Content</h2>
                <h3>4.1 Types of Content</h3>
                <p>Users may save:</p>
                <ul>
                    <li>URLs</li>
                    <li>Tags</li>
                </ul>
                <h3>4.2 Private vs. Public Content</h3>
                <ul>
                    <li><strong>Private content:</strong> accessible only to you and not used for editorial or marketing purposes.</li>
                    <li><strong>Public content:</strong> content explicitly marked as public by the user may be viewed by others and is eligible for the uses described below.</li>
                </ul>
                <h3>4.3 License to Public Content</h3>
                <p>By marking content as <strong>public</strong>, you grant <strong>just bookmark it!</strong> a worldwide, royalty-free, non-exclusive license to:</p>
                <ul>
                    <li>display it publicly</li>
                    <li>share it</li>
                    <li>reproduce it</li>
                    <li>include it in newsletters</li>
                    <li>include it in link discovery pages and recommendations</li>
                    <li>use it in editorial content</li>
                    <li>use it for marketing and promotional materials</li>
                </ul>
                <p>This license applies <strong>only</strong> to content you deliberately mark as public.</p>

                <h2>5. Data Handling & Privacy</h2>
                <h3>5.1 Privacy Policy</h3>
                <p>Our <a href="/privacy" target="_blank">Privacy Policy</a> explains how we collect and process personal data, including email addresses. By using the Service, you also agree to the Privacy Policy.</p>
                <h3>5.2 Access to User Data</h3>
                <p>We will only access private user content when necessary to:</p>
                <ul>
                    <li>comply with legal obligations</li>
                    <li>investigate or prevent abuse</li>
                    <li>maintain or improve the technical operation of the Service (e.g., debugging, data recovery)</li>
                </ul>
                <p>Private content is never accessed for editorial or marketing purposes.</p>
                <h3>5.3 Data Deletion</h3>
                <p>If you request account deletion, contact <a href="mailto:support@justbookmark.it">support@justbookmark.it</a>. All associated data will be deleted within <strong>10 days</strong>, unless retention is required for legal compliance.</p>

                <h2>6. Acceptable Use</h2>
                <p>You agree <strong>not</strong> to use the Service to:</p>
                <ul>
                    <li>Save, link to, or share illegal content, hate speech/offensive materials, malware/phishing, or copyrighted content without authorization</li>
                    <li>Attempt to circumvent security measures</li>
                    <li>Reverse-engineer, decompile, or modify the Service or the iOS app</li>
                    <li>Use bots, scrapers, crawlers, or automated tools without our approval or official API access</li>
                </ul>

                <h2>7. Pricing changes</h2>
                <p>We may introduce different subscription plans (e.g. annual plan, pro plan) in the future or change prices of existing plans. Pricing changes will be communicated in advance, and continued use after changes constitutes acceptance.</p>

                <h2>8. iOS App License</h2>
                <ul>
                    <li>We grant a limited, non-exclusive, non-transferable license to install and use the iOS app on Apple-branded devices you own or control, in accordance with the App Store Terms of Service.</li>
                    <li>Apple is not responsible for the app or its content and has no obligation to provide maintenance or support.</li>
                    <li>Any app-related claims are our responsibility, not Apple’s.</li>
                    <li>Apple and its subsidiaries are third-party beneficiaries to this agreement and may enforce these Terms.</li>
                </ul>

                <h2>9. Intellectual Property</h2>
                <p>All trademarks, branding, interface elements, text, and code are our exclusive property. You may not copy, reproduce, distribute, or modify any portion of the Service without permission.</p>

                <h2>10. Service Availability</h2>
                <p>We may modify, update, or discontinue parts of the Service at any time. We cannot guarantee uninterrupted availability.</p>

                <h2>11. Disclaimers</h2>
                <p>The Service is provided <strong>“as is”</strong>, without warranties of any kind, including accuracy, uninterrupted operation, compatibility, or retention of stored data. Use the Service at your own risk.</p>

                <h2>12. Limitation of Liability</h2>
                <p>To the maximum extent permitted by law:</p>
                <ul>
                    <li>We are not liable for any indirect, incidental, or consequential damages.</li>
                    <li>We are not liable for loss of data, account issues, or system outages.</li>
                    <li>We are not liable for any content or links saved by users.</li>
                    <li>Total liability shall not exceed the amount paid by the user in the past <strong>3 months</strong> (or €0 if the service was free).</li>
                </ul>

                <h2>13. Refunds & Remedies</h2>
                <p>We aim to provide a reliable service, but please note the following regarding refunds and remedies:</p>
                <ul>
                    <li>In general, <strong>we do not offer refunds</strong> for paid subscriptions, including partial months, unless required by law.</li>
                    <li>Refunds may be provided in the following cases:</li>
                    <ul>
                        <li>Duplicate billing or accidental overcharges.</li>
                        <li>Failure to deliver the service you paid for (e.g., technical issues preventing account activation).</li>
                        <li>Cases where local consumer protection laws (such as the EU 14-day withdrawal period) apply and have not been waived by you.</li>
                    </ul>
                    <li>This section is separate from, and does not affect, the <strong>Limitation of Liability</strong> clause. Even if a refund is granted, your total liability remains capped at the amount paid in the past 3 months (or €0 if the service was free).</li>
                </ul>
                <p>To request a refund, contact us at <a href="mailto:support@justbookmark.it">support@justbookmark.it</a> with details of your request.</p>

                <h2>14. Termination</h2>
                <p>We may suspend or terminate your account if you violate these Terms, abuse the Service, or we discontinue the Service. Upon termination, your license to use the Service ends immediately.</p>

                <h2>15. Governing Law</h2>
                <p>These Terms are governed by the laws of <strong>Hungary</strong>, under EU regulation where applicable. Any disputes shall be resolved in Hungarian courts.</p>

                <h2>16. Contact Information</h2>
                <p>For any questions or legal notices: <a href="mailto:support@justbookmark.it">support@justbookmark.it</a></p>
            </div>
        </div>
    </div>
</template>

<script>

import navbar from "@/components/NavBar";

export default {
    name: "TermsOfUse",
    head: {
      title: 'just bookmark it! - terms of use'
    },
    components: {
        navbar
    }
};
</script>

<style scoped>
    body {
        background-color: #f8f9fa;
    }
    .policy-container {
        background: #fff;
        border-radius: 0.5rem;
        padding: 2rem;
        max-width: 800px;
        margin: auto;
        box-shadow: 0 0 10px rgba(0,0,0,0.05);
    }
    h1, h2, h3 {
     margin-top: 1.5rem;
    }
    p, li {
        font-size: 1rem;
        line-height: 1.6;
    }
</style>
